import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';

const identities = [
  {
    id: 'NEWS_JOURNALIST',
    label: 'Informador (News Journalist)',
    description: 'Cobertura informativa, notas y verificación de fuentes.',
    icon: 'fa-newspaper',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
  },
  {
    id: 'MASTER_EDUCATOR',
    label: 'Maestro (Master Educator)',
    description: 'Cursos, lecciones y biblioteca de aprendizaje.',
    icon: 'fa-graduation-cap',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
  },
  {
    id: 'LIFESTYLE_INFLUENCER',
    label: 'Influencer (Lifestyle Influencer)',
    description: 'Campañas, audiencia y colaboraciones de marca.',
    icon: 'fa-bullhorn',
    color: 'text-purple-700',
    bg: 'bg-purple-100',
  },
  {
    id: 'PRO_SPECIALIST',
    label: 'Especialista (Pro Specialist)',
    description: 'Servicios profesionales con contrato digital.',
    icon: 'fa-briefcase',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
  },
];

const CreatorIdentityProvisioningModal = ({ isOpen, isSubmitting = false, onClose, onSelect }) => {
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;

    const body = document.body;
    const currentLocks = Number(body.dataset.overlayLocks || '0');
    body.dataset.overlayLocks = String(currentLocks + 1);
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';

    return () => {
      const locks = Math.max(0, Number(body.dataset.overlayLocks || '1') - 1);
      if (locks === 0) {
        delete body.dataset.overlayLocks;
        body.style.overflow = '';
        body.style.touchAction = '';
      } else {
        body.dataset.overlayLocks = String(locks);
      }
    };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="identity-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed top-0 left-0 w-screen h-screen z-[2147483646] bg-black/70 backdrop-blur-[5px] grid place-items-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 8 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="px-6 py-5 border-b bg-gradient-to-r from-slate-900 to-slate-700 text-white">
              <h2 className="text-lg font-bold">Activación de Herramientas de Creador</h2>
              <p className="text-xs opacity-90 mt-1">Selecciona tu identidad profesional para provisionar tu suite de Ágora Web.</p>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {identities.map((identity) => (
                <button
                  key={identity.id}
                  onClick={() => !isSubmitting && onSelect(identity.id)}
                  disabled={isSubmitting}
                  className="text-left p-4 border rounded-xl hover:border-blue-400 hover:shadow-md transition-all disabled:opacity-60"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${identity.bg} ${identity.color}`}>
                      <i className={`fas ${identity.icon}`}></i>
                    </div>
                    <h3 className="font-bold text-gray-800">{identity.label}</h3>
                  </div>
                  <p className="text-xs text-gray-600">{identity.description}</p>
                </button>
              ))}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="text-xs px-4 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-60"
              >
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CreatorIdentityProvisioningModal;
